import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { Action } from '../../types/toolbar';
import { Toolbar } from '../../components/ui/toolbar/toolbar.component';
import CustomModal from '../../components/ui/modal/modal.component';
import DynamicForm from '../../components/ui/dynamicForm/dynamicForm.component';
import { useCRUD } from '../../api/crud';
import { NUMERATION_SCHEMA_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from '../../consts';
import { useHasPermission } from '../../contexts/auth';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

export const TenantNumerationSchemaActions = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(NUMERATION_SCHEMA_ENDPOINT);
  const [editSchemaOpened, setEditSchemaOpened] = useState<boolean>(false);
  const [deleteSchemaOpened, setDeleteSchemaOpened] = useState<boolean>(false);
  const canChangeSchema = useHasPermission("settlement.change_numerationschema");
  const canDeleteSchema = useHasPermission("settlement.delete_numerationschema");

  const handleDeleteSchema = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Numeration schema deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteSchemaOpened(false);
      window.dispatchEvent(new Event("numerationSchemasUpdated"));
      navigate('/tenant/numeration-schemas');
    } else {
      enqueueSnackbar(t('Numeration schema deletion failed'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t('Edit numeration schema'),
      onClick: () => {
        setEditSchemaOpened(true);
      },
      disabled: !canChangeSchema,
    },
    {
      icon: <DeleteIcon />,
      label: t('Delete numeration schema'),
      onClick: () => {
        setDeleteSchemaOpened(true);
      },
      disabled: !canDeleteSchema,
      color: "var(--errorColor)",
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this numeration schema will permanently remove it from the system.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteSchema}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editSchemaOpened}
        setOpened={setEditSchemaOpened}
        title={t('Edit numeration schema')}
        content={
          <DynamicForm
            url={NUMERATION_SCHEMA_ENDPOINT}
            id={id}
            onSuccess={() => {
              setEditSchemaOpened(false);
              window.dispatchEvent(new Event("numerationSchemasUpdated"));
            }}
            fieldAttributes={{
              template: {
                helperText: (
                  <>
                    {t("Template uses placeholders for the number. COUNTER is required. Example: CT/{COUNTER}/{YEAR}/{MONTH}/{DAY}")}
                    <br />
                    <strong>{t("Available tags")}:</strong>
                    <br />
                    {"{"}COUNTER{"}"} — {t("sequential number (required)")}
                    <br />
                    {"{"}YEAR{"}"} — {t("year")}
                    <br />
                    {"{"}MONTH{"}"} — {t("month")}
                    <br />
                    {"{"}DAY{"}"} — {t("day")}
                  </>
                ),
              },
            }}
          />
        }
      />
      <CustomModal
        opened={deleteSchemaOpened}
        setOpened={setDeleteSchemaOpened}
        title={t('Delete numeration schema')}
        content={deleteModalContent}
      />
    </>
  );
};
